// Import Dependencies
import { Outlet, ScrollRestoration } from "react-router";
import { lazy, useEffect } from "react";

import { SplashScreen } from "@/components/template/SplashScreen";
import { Loadable } from "@/components/shared/Loadable";
import { Progress } from "@/components/template/Progress";
import { useAuthStore } from "@/store/useAuthStore";
import { useChatStore } from "@/store/useChatsStore";
import useRequestsStore from "@/store/useRequestStore";
import Toaster from "@/components/template/Toaster";
import { useSupervisorSocket } from "@/hooks/useSupervisorSocket";
// const Customizer = Loadable(
//   lazy(() => import("components/template/Customizer")),
// );
const Tooltip = Loadable(lazy(() => import("@/components/template/Tooltip")));

// ----------------------------------------------------------------------

function SupervisorSocketManager() {
  const user = useAuthStore((state) => state.user);
  const isSupervisor = user?.roleName === "Support Center Supervisor";

  // Only call hook if user is supervisor
  if (isSupervisor) {
    return <SupervisorSocketProxy />;
  }
  return null;
}

function SupervisorSocketProxy() {
  useSupervisorSocket();
  return null;
}

function Root() {
  const { isInitialized } = useAuthStore();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const { chats } = useChatStore.getState();
      const { acceptedChats } = useRequestsStore.getState();

      const hasActiveChatsInStore = Object.values(chats).some(
        (chat) => chat.status === "active"
      );

      const hasAcceptedChats = acceptedChats.length > 0;

      const hasActiveChats = hasActiveChatsInStore || hasAcceptedChats;

      if (!hasActiveChats) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    const handlePageHide = () => {
      const { chats, clearAllChats } = useChatStore.getState();
      const { acceptedChats, clearAll } = useRequestsStore.getState();

      const hasActiveChatsInStore = Object.values(chats).some(
        (chat) => chat.status === "active"
      );

      const hasAcceptedChats = acceptedChats.length > 0;

      const hasActiveChats = hasActiveChatsInStore || hasAcceptedChats;

      if (!hasActiveChats) {
        return;
      }

      // Clear all chats and accepted chat references only when navigation actually happens
      clearAllChats();
      clearAll();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  if (!isInitialized) {
    return <SplashScreen />;
  }

  return (
    <>
      <Progress />
      <ScrollRestoration />
      <Outlet />
      <Tooltip />
      <Toaster />
      <SupervisorSocketManager />
      {/* <Customizer /> */}
    </>
  );
}

export default Root;
